import isNil from 'lodash-es/isNil';
import isNumber from 'lodash-es/isNumber';
import { runInAction } from 'mobx';

import {
  type CatalogBadgeAPI,
  CatalogRequests,
  type MultibuyPriceLevel,
  type ProductAPI,
  type ProductCommodityGroupAPI,
  type ProductStatus,
  type RatingDistributionAPI,
  type RatingHistogramAPI,
  type Variant,
  type WarehouseStockAPI,
} from '~/api/Catalog';
import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { userStore } from '~/stores';
import {
  type MixAndMatchInfo,
  parseCatalogBadges,
  parseProperties,
  parseVariantOffers,
  parseVariants,
  resolveDisplayPrices,
  resolveMixAndMatch,
} from '~/types/Product';
import type {
  ProductProperties,
  VariantOffer,
} from '~/types/Product/interface';
import {
  catalogWarehouseParam,
  maxWarehouseSellable,
} from '~/utils/catalogWarehouse';
import {
  calcDiscountPercentage,
  convertPenceToPounds,
  formatRating,
} from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
  isMultibuyActive,
  normalizeCatalogMultibuyLevels,
  sortMultibuyLevels,
} from '~/utils/multibuy';

import { Offer } from './Offer';

export interface LoadProductParams {
  productId: string | number;
  lang?: string;
  /** SSR only: the warehouse from the request cookie; the client reads the store. */
  warehouse?: string;
  sku?: string;
}

export class Product {
  public readonly id: number;
  public readonly productId: number;
  public readonly name: string;
  public readonly priceFormatted: string;
  public readonly discountPriceFormatted: string;
  public readonly salesQuantity: number;
  public readonly ratingAverage: number;
  public readonly ratingMarksCount: number;
  public readonly ratingDistribution?: RatingDistributionAPI;
  public readonly ratingHistogram?: RatingHistogramAPI | null;
  public readonly productType: 'product' | 'bundle';
  public readonly sku: Nullable<string>;
  public readonly code: Nullable<string>;
  public readonly slug: string;
  public readonly sellableWarehouses: Record<string, string>;
  /** Priority-ordered per-warehouse stock; null on legacy responses. */
  public readonly stocks: Nullable<WarehouseStockAPI[]>;
  public readonly status: Nullable<ProductStatus>;
  public readonly categoryId: number;
  public readonly categoryName: string;
  public readonly categorySlug: string;
  public readonly parentCategoryId: number;
  public readonly description: string;
  public readonly productSlug: string;
  public readonly isBackorderAvailable: boolean;
  public readonly createdAt: string;
  public readonly metadataTitle: Nullable<string>;
  public readonly metadataDescription: Nullable<string>;
  public readonly variants: Variant[];
  public readonly properties: ProductProperties;
  /**
   * Badges from the catalog badge reference book, in display order.
   * Offers inherit these unless they carry badges of their own.
   */
  public readonly catalogBadges: CatalogBadgeAPI[];
  public readonly offers: Offer[];
  public readonly variantOffers: VariantOffer[];
  public readonly previewImage: string;
  public readonly previewImageThumb: string;
  public readonly images: string[];
  public readonly backorderLeadTime: number;
  public readonly multibuyPriceLevels: MultibuyPriceLevel[];
  /** Lowest Mix & Match price among the variants: the "from" price. */
  public readonly mixAndMatch: Nullable<MixAndMatchInfo>;
  public readonly pricePerUnit: string;
  public readonly discountPercentage: string;
  public readonly commodityGroup: Nullable<{
    id: number;
    name: ProductCommodityGroupAPI;
  }>;
  public readonly isBought: boolean;

  public price: number;
  public discountPrice: number;
  public discountAmount: number;
  /**
   * Member discount per unit from the last cart calculate, in pence; already
   * inside `discountPrice`. Unset outside the cart and for non-members.
   */
  public membershipDiscountAmount?: number;
  public sellable: number;

  constructor(product: ProductAPI) {
    const { price, discountPrice } = resolveDisplayPrices(product);

    this.id = product.id;
    this.productId = product.id;
    this.name = product.name;
    this.price = price;
    this.priceFormatted = convertPenceToPounds(this.price);
    this.salesQuantity = product.salesQuantity || 0;
    this.discountPrice = discountPrice;
    this.discountAmount =
      discountPrice > 0 ? Math.max(0, this.price - discountPrice) : 0;
    this.discountPriceFormatted = convertPenceToPounds(this.discountPrice);
    this.mixAndMatch = company.mixAndMatchEnabled
      ? resolveMixAndMatch(product, discountPrice > 0 ? discountPrice : price)
      : null;
    this.ratingAverage = formatRating(+(product.ratingAverage ?? 0));
    this.ratingMarksCount = product.ratingMarksCount || 0;
    this.ratingDistribution = product.ratingDistribution;
    this.ratingHistogram = product.ratingHistogram;
    this.productType = product.productType as 'product' | 'bundle';
    this.sku = product.code;
    this.code = product.code;
    this.slug = product.slug;
    this.sellable =
      product.sellable ??
      (company.hasNewLogistics
        ? maxWarehouseSellable(product.sellableWarehouses)
        : 0);
    this.sellableWarehouses = product.sellableWarehouses || {};
    this.stocks = product.stocks ?? null;
    this.status = product.status;
    this.categoryId = product.categoryId || -1;
    this.categoryName = product.categoryName || '';
    this.categorySlug = product.categorySlug || '';
    this.parentCategoryId = product.parentCategoryId || -1;
    this.description = product.description || '';
    this.productSlug = product.slug;
    this.isBackorderAvailable = !!product.isBackorderAvailable;
    this.createdAt = product.createdAt || '';
    this.metadataTitle = product.metadataTitle;
    this.metadataDescription = product.metadataDescription;
    this.variants = product.variants || [];
    this.properties = product.offerProperties?.length
      ? parseProperties(product.offerProperties)[0]
      : parseVariants(product.variants || []);
    this.commodityGroup = product.commodityGroup;
    // Must be assigned before offers are constructed — each offer falls back to
    // the product's badges when it has none of its own.
    this.catalogBadges = parseCatalogBadges(product.badges);
    this.offers = (product.offers || [])
      .filter((data) => Offer.validate(data, this))
      .map((data) => new Offer(data, this));
    this.variantOffers = parseVariantOffers(product.variants);

    this.images = (product.images || [])
      .map(({ url, thumb }) => url || thumb || '')
      .filter(Boolean);
    this.previewImage = product.previewImage?.url || this.images[0] || '';
    this.previewImageThumb =
      product.previewImageThumb?.thumb ||
      product.previewImageThumb?.url ||
      product.previewImage?.thumb ||
      product.previewImageThumb?.url ||
      this.images[0] ||
      '';

    const firstOffer = this.offers[0];
    const offersBackorderLeadTime = this.offers
      .map(({ backorderLeadTime }) => backorderLeadTime)
      .filter(isNumber);

    this.backorderLeadTime = offersBackorderLeadTime.length
      ? Math.max(...offersBackorderLeadTime)
      : product.backorderLeadTime || 0;
    // Get multibuyPriceLevels from the first offer (an Offer instance whose
    // levels are already normalized, incl. the converted unit price) or from
    // the raw product. Levels are used whenever the backend provides them
    // (data-driven); the minBasketDiscount* single-level fallback stays for
    // companies without levels.
    this.multibuyPriceLevels = sortMultibuyLevels(
      firstOffer?.multibuyPriceLevels?.length
        ? firstOffer.multibuyPriceLevels
        : (normalizeCatalogMultibuyLevels(product.multibuyPriceLevels) ??
            (company.name === CompanyName.CityDrinks
              ? []
              : product.minBasketDiscountPrice &&
                  product.minBasketDiscountQuantity
                ? [
                    {
                      id: 1,
                      quantity: product.minBasketDiscountQuantity,
                      unitPrice: product.minBasketDiscountPrice,
                    },
                  ]
                : null)),
    );
    this.pricePerUnit = firstOffer?.pricePerUnit || '';
    const discount = calcDiscountPercentage(this.price, this.discountPrice);
    this.discountPercentage = discount ? `-${discount}%` : '';
    this.isBought = product.isBought || false;
  }

  public static compareId(id: string | number, product: ProductAPI | Product) {
    return String(id) === product.slug || Number(id) === product.id;
  }

  public static validate(product: ProductAPI) {
    // Use the resolved display price (converted when available) so converted-price
    // products, whose raw `price` may be empty, are not filtered out of search/catalog.
    const { price } = resolveDisplayPrices(product);

    return !isNil(product.id) && Boolean(product.name) && price > 0;
  }

  /**
   * Offer a list card opens on. LMS lists look across warehouses: the first
   * offer is often a sold-out size while a sibling can be bought.
   */
  get defaultOffer(): Offer | undefined {
    if (!company.hasNewLogistics) {
      return this.offers[0];
    }
    return (
      this.offers.find(
        (offer) => maxWarehouseSellable(offer.sellableWarehouses) > 0,
      ) ?? this.offers[0]
    );
  }

  /** The offer a listing without `offers[]` presents (its `code`), when the catalogue also lists it as a variant. */
  get presentedVariantSku(): Nullable<string> {
    return this.variantOffers.some(({ sku }) => sku === this.code)
      ? this.code
      : null;
  }

  get skus(): string[] {
    return Array.from(
      new Set([
        ...this.offers.map(({ sku }) => sku),
        ...this.variantOffers.map(({ sku }) => sku),
      ]),
    );
  }

  public static async requestById(
    productId: string | number,
    sku?: string,
  ): Promise<Product> {
    const { data } = await getProductById({ productId, sku });

    return new Product(data);
  }

  public static async loadFullOffer(
    product: Product,
    sku: string,
    lang?: string,
  ): Promise<Offer> {
    const { data } = await CatalogRequests.getFullOffer(
      {
        ...catalogWarehouseParam(),
        sku,
      },
      lang,
    );
    const raw = data.find((item) => item.code === sku);

    if (!raw || !Offer.validate(raw, product)) {
      throw new Error('Offer not found');
    }

    const offer = new Offer(raw, product);

    runInAction(() => {
      Object.assign(product, {
        offers: [...product.offers.filter((item) => item.sku !== sku), offer],
      });
    });

    return offer;
  }

  public static async loadOffer(product: Product, sku: string) {
    const { data } = await CatalogRequests.getOffers({
      ...catalogWarehouseParam(),
      productId: product.id,
      sku,
    });

    const offersMap = new Map(
      product.offers.map((offer) => [offer.sku, offer]),
    );

    data.forEach((item) => {
      // The offers endpoint omits the converted prices the products endpoint
      // returns, so for converted-price companies a reloaded offer would fall
      // back to base-currency amounts — mixing currencies in the price and
      // producing a nonsensical discount badge.
      const previous = offersMap.get(item.code);
      const raw =
        company.convertedPricesEnabled && previous && item.salePrice == null
          ? {
              ...item,
              salePrice: previous.price,
              convertedDiscountPrice: previous.discountPrice || null,
            }
          : item;

      offersMap.set(item.code, new Offer(raw, product));
    });

    runInAction(() => {
      Object.assign(product, { offers: Array.from(offersMap.values()) });
    });

    return offersMap;
  }

  /**
   * Возвращает наивысший применимый уровень multibuy для заданного количества.
   */
  public getApplicableMultibuyLevel(
    quantity: number,
  ): MultibuyPriceLevel | null {
    return getApplicableMultibuyLevel(this.multibuyPriceLevels, quantity);
  }

  /**
   * Возвращает минимальный уровень multibuy (с наименьшим quantity).
   */
  public getLowestMultibuyLevel(): MultibuyPriceLevel | null {
    return getLowestMultibuyLevel(this.multibuyPriceLevels);
  }

  /**
   * Проверяет, активен ли multibuy для заданного количества.
   */
  public isMultibuyActive(quantity: number): boolean {
    return isMultibuyActive(this.multibuyPriceLevels, quantity);
  }
}

export const getProductById = (
  { productId, warehouse, sku }: LoadProductParams,
  lang?: string,
) => {
  return CatalogRequests.getProduct(
    String(productId),
    {
      ...catalogWarehouseParam(warehouse),
      deviceId: userStore.deviceId,
      customerId: userStore.isAuthorized
        ? userStore.personalData.id || undefined
        : undefined,
      ...(company.compactProductOffersEnabled
        ? { compactOffers: true, sku: sku || undefined }
        : {}),
    },
    lang,
  );
};
